import { useEffect, useRef } from 'react';
import { useWebSockets } from '@innovyze/lib-am-common/Utils';

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type Handler = (data: any) => void;
interface Handlers {
	results: Handler;
	errors: Handler;
}

// lib-am-common's websocket is a session-wide singleton and its setupRoutes
// keeps the FIRST handler registered for a route, forever. Registering a
// component's callback directly therefore pins that render's closure (e.g.
// the first model's configId) for the rest of the session. Instead we
// register one forwarder per route that calls whichever mounted instance
// registered most recently, with its latest handlers. When none is mounted,
// messages go to the last-supplied `whileUnmounted` handler (if any), which
// must not depend on component state.
const activeHandlers = new Map<string, { current: Handlers }>();
const unmountedHandlers = new Map<string, { current?: Handler }>();

export const useWebSocketRoute = (
	route: string,
	onResults: Handler,
	onError: Handler,
	enabled = true,
	whileUnmounted?: Handler,
): void => {
	const webSocket = useWebSockets();
	const handlers = useRef<Handlers>({ results: onResults, errors: onError });
	handlers.current = { results: onResults, errors: onError };
	const unmounted = useRef<Handler | undefined>(whileUnmounted);
	unmounted.current = whileUnmounted;

	useEffect(() => {
		if (!enabled) return undefined;
		activeHandlers.set(route, handlers);
		unmountedHandlers.set(route, unmounted);
		webSocket.setupRoutes(
			route,
			(data: unknown) => {
				const active = activeHandlers.get(route);
				if (active) active.current.results(data);
				else unmountedHandlers.get(route)?.current?.(data);
			},
			(data: unknown) => activeHandlers.get(route)?.current.errors(data),
		);
		return () => {
			if (activeHandlers.get(route) === handlers)
				activeHandlers.delete(route);
		};
	}, [route, enabled]);
};
